import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BaseService} from './base.service';
import {Observable} from 'rxjs';
import {AlertService} from './alert.service';
import {GlobalService} from './global.service';
import {share} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UploadService extends BaseService {
  progress$;
  progressObserver: any;
  progress: any;

  constructor(public http: HttpClient,
              public alertService: AlertService,
              public globalService: GlobalService) {
    super(http, alertService, globalService);
    this.progress$ = Observable.create((observer: any) => {
      this.progressObserver = observer;
    }).pipe(share());
  }

  fileUpload(url: any, params: any, event: Event): Observable<any> {
    let files: any;
    const element: HTMLInputElement = <HTMLInputElement>event.srcElement;
    if (params['fileType'] === 'NORMAL') {
      files = element && element.files ? element.files : '';
    } else {
      files = event;
    }
    return this.makeFileRequest(files, url, params);
  }

  makeFileRequest(files: any, url: string, params: any): Observable<any> {
    return Observable.create((observer: any) => {
      const formData: FormData = new FormData();
      const xhr: XMLHttpRequest = new XMLHttpRequest();

      for (const param in params) {
        formData.append(param, params[param]);
      }
      formData.append('fileType', params.fileType);

      if (params.fileType === 'NORMAL') {
        formData.append('file', files[0], files[0].name);
      } else {
        formData.append('file', files);
      }


      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            observer.next(JSON.parse(xhr.response));
            observer.complete();
          } else {
            observer.error(xhr.response);
          }
        }
      };

      xhr.upload.onprogress = (event) => {
        this.progress = Math.round(event.loaded / event.total * 100);
      };

      xhr.open('POST', url, true);
      if (this.globalService.getAccessToken()) {
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader('device', 'WEB');
        xhr.setRequestHeader('Authorization', 'Bearer ' + this.globalService.getAccessToken());
      }

      xhr.send(formData);
    });
  }

  /**
   * Direct-to-S3 chat attachment uploads (see FileRepository::
   * initiateMessageFileUpload on the backend) -- the file body goes
   * straight to the presigned S3 URL, no FormData/auth headers, since S3
   * authorizes the request via the URL's own signature. Unlike
   * makeFileRequest/makeMultipleFileRequest above (which wire
   * xhr.upload.onprogress into a shared, never-consumed scalar), this
   * actually emits progress through the returned Observable, and
   * unsubscribing calls xhr.abort() for real upload cancellation.
   */
  uploadDirectToS3(
    file: Blob,
    presignedUrl: string,
    contentType: string,
    onProgress?: (percent: number) => void
  ): Observable<{ etag: string | null }> {
    return new Observable((observer) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (onProgress && event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            observer.next({ etag: xhr.getResponseHeader('ETag') });
            observer.complete();
          } else {
            observer.error(xhr.response || xhr.status);
          }
        }
      };

      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file);

      return () => {
        if (xhr.readyState !== 4) {
          xhr.abort();
        }
      };
    });
  }

  makeMultipleFileRequest(files: any, url: string, params: any): Observable<any> {
    return Observable.create((observer: any) => {
      const formData: FormData = new FormData();
      const xhr: XMLHttpRequest = new XMLHttpRequest();

      for (const param in params) {
        formData.append(param, params[param]);
      }
      formData.append('fileType', params.fileType);

      if (params.fileType === 'NORMAL') {
        formData.append('file', files, files.name);
      } else {
        formData.append('file', files);
      }


      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            observer.next(JSON.parse(xhr.response));
            observer.complete();
          } else {
            observer.error(xhr.response);
          }
        }
      };

      xhr.upload.onprogress = (event) => {
        this.progress = Math.round(event.loaded / event.total * 100);
      };

      xhr.open('POST', url, true);
      if (this.globalService.getAccessToken()) {
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader('device', 'WEB');
        xhr.setRequestHeader('Authorization', 'Bearer ' + this.globalService.getAccessToken());
      }

      xhr.send(formData);
    });
  }
}
